import { MouseEvent, useCallback, useEffect, useState } from 'react'
import Icon from '../Icon/Icon'
import BrandHeader from './hooks/BrandHeader'

import { useRecaptchaV3 } from '@/hooks/useRecaptchaV3'
import {
  MobileVerificationUpdatePayload,
  useMobileVerificationCreateApi,
  useMobileVerificationUpdateApi,
} from './hooks/useMobileVerificationApi'
import VerificationNumber from './hooks/VerificationNumber'
import VerificationCode from './VerificationCode'

interface MobileVerifyProps {
  brand: string
  phoneNumber: string
  guestId?: string | null
  /** Order being verified against — the backend's fallback for attributing
   *  the OTP when there's no session and no usable user-id param. */
  orderId?: string | null
  showPhoneNumber: boolean
  onVerificationSuccess: (
    smsTrackingOptin: boolean,
    agreeToTOS: boolean,
  ) => void
  /** When omitted, the "Skip for now" button is not rendered — verification
   *  is required (e.g. the print-labels gate on order confirmation). */
  onSkipVerification?: () => void
  onGoBack?: () => void
  errorMessage?: string
  showTermsOfService?: boolean
  agreeToTOS?: boolean
  onTOSChanged?: (value: boolean) => void
}

export default function MobileVerify({
  brand,
  phoneNumber,
  guestId,
  orderId,
  showPhoneNumber,
  onVerificationSuccess,
  onSkipVerification,
  onGoBack,
  errorMessage,
  showTermsOfService,
  agreeToTOS,
}: MobileVerifyProps) {
  const [performFirstSend, setPerformFirstSend] = useState(true)
  const [mobileNumber, setMobileNumber] = useState(phoneNumber)
  const [verificationCode, setVerificationCode] = useState<string>('')
  const [smsTrackingOptin, setSmsTrackingOptin] = useState<boolean>(false)
  const [termsValue, setTermsValue] = useState<boolean>(agreeToTOS || false)
  const [verificationError, setVerificationError] = useState(errorMessage)
  const [isSuccess, setIsSuccess] = useState(false)

  const {
    mutate: sendCode,
    data: sendCodeResults,
    error: sendCodeError,
    isPending: sendingCode,
    isSuccess: sendCodeSuccess,
  } = useMobileVerificationCreateApi()

  const {
    mutate: verifyCode,
    data: verifyResults,
    reset: verifyReset,
    error: verifyError,
    isPending: verifying,
  } = useMobileVerificationUpdateApi()

  const { executeRecaptcha } = useRecaptchaV3()

  const handleConfirmVerificationCode = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()

      if (isSuccess || verifying) return

      // Only send smsTrackingOptin when the user checked the box — sending
      // false would wipe a transactional opt-in made elsewhere (My Account,
      // order confirmation).
      const variables = {
        phoneNumber: mobileNumber,
        guestUserId: guestId,
        orderId,
        verificationCode,
        ...(smsTrackingOptin && { smsTrackingOptin }),
      }

      const onError = (error: Error) => setVerificationError(error.message)

      const onSuccess = (payload: MobileVerificationUpdatePayload) => {
        if (payload.success) {
          setIsSuccess(true)
          onVerificationSuccess(smsTrackingOptin, termsValue)
        } else {
          setVerificationError(payload.message)
        }
      }

      verifyCode(variables, { onError, onSuccess })
    },
    [
      guestId,
      orderId,
      isSuccess,
      mobileNumber,
      onVerificationSuccess,
      setVerificationError,
      smsTrackingOptin,
      termsValue,
      verificationCode,
      verifyCode,
      verifying,
    ],
  )

  const sendVerificationCode = useCallback(async () => {
    setVerificationError(undefined)
    verifyReset()
    // Mint an invisible reCAPTCHA v3 token before requesting a code. The action
    // must match the backend guard's action ('mobile_verification'). A null
    // token (SDK blocked/unavailable) is forwarded as undefined; the backend
    // then rejects the send rather than pumping an unverified request.
    const recaptchaToken = await executeRecaptcha('mobile_verification')
    sendCode({
      phoneNumber: mobileNumber,
      recaptchaToken: recaptchaToken ?? undefined,
    })
  }, [mobileNumber, sendCode, verifyReset, executeRecaptcha])

  const handleUpdatePhoneNumber = useCallback(
    // resend the verification code when the phone number
    // is updated
    (newPhoneNumber: string) => {
      setMobileNumber(newPhoneNumber)
      sendVerificationCode()
    },
    [sendVerificationCode],
  )

  useEffect(() => {
    // the first time a user comes to this panel, send the verification
    // code
    if (performFirstSend && mobileNumber?.length > 0) {
      setPerformFirstSend(false)
      sendVerificationCode()
    }
  }, [performFirstSend, mobileNumber, sendVerificationCode])

  const showSendMessage =
    sendCodeSuccess &&
    sendCodeResults?.success &&
    !sendingCode &&
    !sendCodeError &&
    !verifyResults &&
    !verifyError?.message

  const canConfirmCode =
    mobileNumber?.length > 0 &&
    verificationCode?.length == 6 &&
    !verifying &&
    !isSuccess

  return (
    <div className="max-w-md mx-auto">
      {onGoBack && <BrandHeader onGoBack={onGoBack} canGoBack brand={brand} />}
      <h2 className="text-lg font-bold mb-2">Verify your phone number</h2>
      <p className="mb-4 type-body-9 type-body-10 text-gray-500">
        For security purposes, we&apos;ll verify your phone number.
      </p>

      {showPhoneNumber && (
        <VerificationNumber
          onUpdatePhoneNumber={handleUpdatePhoneNumber}
          defaultPhoneNumber={phoneNumber}
          isSending={sendingCode}
        />
      )}
      {showSendMessage && (
        <p className="text-green-600 font-semibold text-sm">
          A 6 digit verification code has been sent to your phone. It will
          expire in 10 minutes. Please enter it below.
        </p>
      )}
      {verificationError && (
        <p className="text-red-600 font-semibold text-sm">
          {verificationError}
        </p>
      )}
      {verifyError?.message && (
        <p className="text-red-600 font-semibold text-sm">
          {'Error verifying number, please try again later'}
        </p>
      )}
      <VerificationCode
        onVerificationCodeChange={(value: string) => setVerificationCode(value)}
        onResendVerificationCode={sendVerificationCode}
      />
      <div className="mt-4 flex items-center space-x-2">
        <input
          type="checkbox"
          name="smsTrackingOptin"
          onChange={(e) => setSmsTrackingOptin(e.target.checked)}
        />
        <label htmlFor="smsTrackingOptin" className="text-sm text-gray-700">
          Receive shipping updates via SMS.
        </label>
      </div>
      {showTermsOfService && (
        <div className="mt-4 flex items-center space-x-2">
          <input
            type="checkbox"
            name="agreeToTOS"
            onChange={(e) => setTermsValue(e.target.checked)}
          />
          <label
            htmlFor="tosCheckbox"
            className="text-xs text-gray-500 mt-2 text-center"
          >
            By creating an account, you agree to our{' '}
            <a target="terms" href="/terms" className="underline">
              Terms of Service
            </a>{' '}
            and{' '}
            <a target="privacy" href="/privacy" className="underline">
              Privacy Policy
            </a>
            .
          </label>
        </div>
      )}
      {errorMessage && (
        <p className="mt-2 text-red-600 font-semibold text-sm">
          {`ERROR: ${errorMessage}`}
        </p>
      )}
      <div className="mt-4 max-w-md mx-auto">
        <button
          type="button"
          disabled={!canConfirmCode}
          onClick={handleConfirmVerificationCode}
          aria-busy={verifying || undefined}
          className="type-body-5 rounded-8 border-2 inline-flex items-center justify-center cursor-pointer transition active:scale-95 disabled:cursor-not-allowed focus:outline-accent-link focus:outline-offset-4 focus:outline-1 min-h-14 md-max:min-h-12 px-4 bg-theme-primary text-white border-theme-primary hover:bg-theme-accent-2 hover:text-white hover:border-theme-accent-2 active:bg-theme-accent-2 active:text-white active:border-theme-accent-2 focus:bg-theme-primary focus:text-white focus:border-theme-primary disabled:bg-neutral-600 disabled:text-white disabled:border-neutral-600 w-full"
        >
          <span className="relative inline-grid place-items-center">
            <span className={verifying ? 'opacity-0' : undefined}>
              {'Confirm'}
            </span>
            {verifying && (
              <Icon
                icon="loading"
                aria-label="Loading"
                className="absolute top-0 size-5 animate-spin"
              />
            )}
          </span>
        </button>

        {onSkipVerification && (
          <button
            type="button"
            disabled={verifying}
            onClick={onSkipVerification}
            className="mt-2 w-full border border-black text-black font-semibold py-3 rounded-lg bg-white4"
          >
            {'Skip for now'}
          </button>
        )}
      </div>
    </div>
  )
}
